import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, ScrollText, Download, Eye, ImageIcon, FileImage, Palette, IdCard, Sun, Moon, Building2, Share2, Instagram, Car } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import Logo from "@/components/Logo";

async function fetchBlobUrl(path) {
  const token = localStorage.getItem("vf_admin_token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Fetch failed");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

async function downloadFile(path, filename) {
  const url = await fetchBlobUrl(path);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function viewFile(path) {
  const url = await fetchBlobUrl(path);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function AdminDocuments() {
  const [pos, setPos] = useState([]);
  const [selectedPO, setSelectedPO] = useState("");
  const [busy, setBusy] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoVariant, setLogoVariant] = useState("dark"); // "dark" | "light"

  // Business card form
  const [bcName, setBcName] = useState("");
  const [bcTitle, setBcTitle] = useState("Sales Consultant");
  const [bcEmail, setBcEmail] = useState("");
  const [bcPhone, setBcPhone] = useState("");

  // Google Business Profile / square logo
  const [gbpBg, setGbpBg] = useState("white"); // white | gold | dark | transparent
  const [gbpPreviewUrl, setGbpPreviewUrl] = useState("");

  // Social banner (1200 × 630)
  const [banBg, setBanBg] = useState("dark");
  const [banTagline, setBanTagline] = useState("Drive now. Own outright.");
  const [banSubline, setBanSubline] = useState("Rent-to-own vehicles for Australian ABN holders.");
  const [banPreviewUrl, setBanPreviewUrl] = useState("");

  // Instagram post
  const [igFormat, setIgFormat] = useState("square"); // square | portrait | story
  const [igBg, setIgBg] = useState("dark");
  const [igHeadline, setIgHeadline] = useState("Drive now.\nOwn outright.");
  const [igSubline, setIgSubline] = useState("No credit checks. 24–48 hour approval.");
  const [igCta, setIgCta] = useState("APPLY AT KEYSTOOWN.AU");
  const [igPreviewUrl, setIgPreviewUrl] = useState("");

  // Vehicle spotlight post
  const [vehicles, setVehicles] = useState([]);
  const [spotVehicleId, setSpotVehicleId] = useState("");
  const [spotFormat, setSpotFormat] = useState("portrait");
  const [spotHeadline, setSpotHeadline] = useState("");
  const [spotHighlightIdx, setSpotHighlightIdx] = useState(-1); // -1 = show all highlights joined
  const [spotCta, setSpotCta] = useState("APPLY AT KEYSTOOWN.AU");
  const [spotPreviewUrl, setSpotPreviewUrl] = useState("");

  useEffect(() => {
    api.get("/admin/purchase-orders").then((r) => setPos(r.data || [])).catch(() => {});
    api.get("/vehicles")
      .then((r) => {
        const list = r.data || [];
        setVehicles(list);
        setSpotVehicleId((prev) => prev || (list[0]?.id ?? ""));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let url = "";
    let cancelled = false;
    setLogoPreviewUrl("");
    (async () => {
      try {
        url = await fetchBlobUrl(`/admin/brand/logo.png?width=1200&variant=${logoVariant}`);
        if (!cancelled) setLogoPreviewUrl(url);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [logoVariant]);

  useEffect(() => {
    let url = "";
    let cancelled = false;
    setGbpPreviewUrl("");
    (async () => {
      try {
        url = await fetchBlobUrl(`/admin/brand/square-logo.png?size=600&background=${gbpBg}`);
        if (!cancelled) setGbpPreviewUrl(url);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [gbpBg]);

  // Debounced social-banner preview loader (tagline/subline typing shouldn't hammer the endpoint)
  useEffect(() => {
    let url = "";
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          width: "1200",
          background: banBg,
          tagline: banTagline || "Drive now. Own outright.",
          subline: banSubline || "Rent-to-own vehicles for Australian ABN holders.",
        });
        url = await fetchBlobUrl(`/admin/brand/social-banner.png?${params.toString()}`);
        if (!cancelled) setBanPreviewUrl(url);
      } catch {
        /* silent */
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [banBg, banTagline, banSubline]);

  // Debounced Instagram post preview loader
  useEffect(() => {
    let url = "";
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          width: "900",
          format: igFormat,
          background: igBg,
          headline: igHeadline || "Drive now.\nOwn outright.",
          subline: igSubline || "",
          cta: igCta || "",
        });
        url = await fetchBlobUrl(`/admin/brand/instagram-post.png?${params.toString()}`);
        if (!cancelled) setIgPreviewUrl(url);
      } catch {
        /* silent */
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [igFormat, igBg, igHeadline, igSubline, igCta]);

  // Debounced Vehicle Spotlight preview loader — heavier (remote image fetch), 600ms debounce.
  useEffect(() => {
    if (!spotVehicleId) return undefined;
    let url = "";
    let cancelled = false;
    setSpotPreviewUrl("");
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          vehicle_id: spotVehicleId,
          format: spotFormat,
          width: "900",
          headline: spotHeadline || "",
          highlight_index: String(spotHighlightIdx),
          cta: spotCta || "",
        });
        url = await fetchBlobUrl(`/admin/brand/vehicle-spotlight.png?${params.toString()}`);
        if (!cancelled) setSpotPreviewUrl(url);
      } catch {
        /* silent */
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [spotVehicleId, spotFormat, spotHeadline, spotHighlightIdx, spotCta]);

  const grab = async (kind, action) => {
    setBusy(`${kind}-${action}`);
    try {
      if (kind === "plan") {
        const path = "/admin/documents/business-plan/pdf";
        if (action === "view") {
          await viewFile(path);
        } else {
          await downloadFile(path, "Keys-To-Own-Business-Plan.pdf");
          toast.success("Business plan downloaded");
        }
      } else if (kind === "lease") {
        const qs = selectedPO ? `?po_id=${selectedPO}` : "";
        const path = `/admin/documents/lease-agreement/pdf${qs}`;
        const po = pos.find((p) => p.id === selectedPO);
        const label = po ? po.po_number : "TEMPLATE";
        if (action === "view") {
          await viewFile(path);
        } else {
          await downloadFile(path, `Operating-Lease-Agreement-${label}.pdf`);
          toast.success("Lease agreement downloaded");
        }
      } else if (kind === "logo-pdf") {
        const label = logoVariant === "light" ? "-Knockout" : "";
        await downloadFile(`/admin/brand/logo.pdf?variant=${logoVariant}`, `Keys-To-Own-Logo${label}.pdf`);
        toast.success(`Logo PDF downloaded${logoVariant === "light" ? " (knockout)" : ""}`);
      } else if (kind === "logo-png") {
        const label = logoVariant === "light" ? "-Knockout" : "";
        await downloadFile(`/admin/brand/logo.png?width=2400&variant=${logoVariant}`, `Keys-To-Own-Logo${label}.png`);
        toast.success(`Logo PNG downloaded (2400px${logoVariant === "light" ? ", knockout" : ""})`);
      } else if (kind === "logo-svg") {
        const label = logoVariant === "light" ? "-Knockout" : "";
        await downloadFile(`/admin/brand/logo.svg?variant=${logoVariant}`, `Keys-To-Own-Logo${label}.svg`);
        toast.success(`Logo SVG downloaded${logoVariant === "light" ? " (knockout)" : ""}`);
      } else if (kind === "cards") {
        if (!bcName.trim()) {
          toast.error("Please enter a name for the cards.");
          return;
        }
        const params = new URLSearchParams({
          name: bcName.trim(),
          title: bcTitle.trim(),
          email: bcEmail.trim(),
          phone: bcPhone.trim(),
        });
        const safeName = bcName.trim().replace(/[^\w -]/g, "").replace(/\s+/g, "-");
        await downloadFile(
          `/admin/brand/business-cards.pdf?${params.toString()}`,
          `Keys-To-Own-Business-Cards-${safeName || "Team"}.pdf`,
        );
        toast.success("Business cards ready. 10 per A4 sheet.");
      } else if (kind === "gbp-png") {
        const bgLabel = gbpBg === "white" ? "" : `-${gbpBg.charAt(0).toUpperCase() + gbpBg.slice(1)}`;
        await downloadFile(
          `/admin/brand/square-logo.png?size=1024&background=${gbpBg}`,
          `Keys-To-Own-Square-Logo${bgLabel}-1024px.png`,
        );
        toast.success("Square PNG downloaded (1024 × 1024)");
      } else if (kind === "gbp-pdf") {
        const bgLabel = gbpBg === "white" ? "" : `-${gbpBg.charAt(0).toUpperCase() + gbpBg.slice(1)}`;
        await downloadFile(
          `/admin/brand/square-logo.pdf?background=${gbpBg}`,
          `Keys-To-Own-Square-Logo${bgLabel}.pdf`,
        );
        toast.success("Square PDF downloaded");
      } else if (kind === "banner") {
        const bgLabel = banBg === "dark" ? "" : `-${banBg.charAt(0).toUpperCase() + banBg.slice(1)}`;
        const params = new URLSearchParams({
          width: "1200",
          background: banBg,
          tagline: banTagline || "Drive now. Own outright.",
          subline: banSubline || "Rent-to-own vehicles for Australian ABN holders.",
        });
        await downloadFile(
          `/admin/brand/social-banner.png?${params.toString()}`,
          `Keys-To-Own-Social-Banner${bgLabel}-1200px.png`,
        );
        toast.success("Social banner downloaded (1200 × 630)");
      } else if (kind === "instagram") {
        const bgLabel = igBg === "dark" ? "" : `-${igBg.charAt(0).toUpperCase() + igBg.slice(1)}`;
        const fmtLabel = igFormat.charAt(0).toUpperCase() + igFormat.slice(1);
        const params = new URLSearchParams({
          width: "1080",
          format: igFormat,
          background: igBg,
          headline: igHeadline || "Drive now.\nOwn outright.",
          subline: igSubline || "",
          cta: igCta || "",
        });
        await downloadFile(
          `/admin/brand/instagram-post.png?${params.toString()}`,
          `Keys-To-Own-Instagram-${fmtLabel}${bgLabel}-1080px.png`,
        );
        const dims = igFormat === "square" ? "1080 × 1080"
          : igFormat === "portrait" ? "1080 × 1350"
          : "1080 × 1920";
        toast.success(`Instagram ${igFormat} downloaded (${dims})`);
      } else if (kind === "spotlight") {
        if (!spotVehicleId) {
          toast.error("Please pick a vehicle.");
          return;
        }
        const fmtLabel = spotFormat.charAt(0).toUpperCase() + spotFormat.slice(1);
        const params = new URLSearchParams({
          vehicle_id: spotVehicleId,
          format: spotFormat,
          width: "1080",
          headline: spotHeadline || "",
          highlight_index: String(spotHighlightIdx),
          cta: spotCta || "",
        });
        await downloadFile(
          `/admin/brand/vehicle-spotlight.png?${params.toString()}`,
          `Keys-To-Own-Spotlight-${spotVehicleId}-${fmtLabel}-1080px.png`,
        );
        toast.success("Vehicle spotlight downloaded");
      }
    } catch {
      toast.error("Failed — please try again");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-documents-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808]">
        <div className="max-w-5xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37]"
          >
            <ArrowLeft size={14} /> Back to admin
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-10">
          <div className="label-eyebrow mb-3">Utilities</div>
          <h1 className="font-display text-4xl md:text-5xl text-white" style={{ fontWeight: 300 }}>
            Business <span className="italic text-[#D4AF37]">Documents</span>
          </h1>
          <p className="text-white/60 mt-4 max-w-2xl">
            Download the Keys To Own business plan and generate an Operating Lease Agreement
            (pre-filled from any Purchase Order).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Plan */}
          <div className="bg-[#0a0a0a] border border-white/10 p-6 flex flex-col">
            <div className="text-[#D4AF37] mb-4"><FileText size={24} /></div>
            <h2 className="font-display text-2xl text-white mb-2" style={{ fontWeight: 400 }}>
              Business Plan
            </h2>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              10-section plan covering market, unit economics, 3-year projections and risk analysis
              — grounded in the actual numbers on your live site.
            </p>
            <div className="mt-auto grid grid-cols-2 gap-2">
              <button
                onClick={() => grab("plan", "view")}
                disabled={busy === "plan-view"}
                data-testid="doc-business-plan-view"
                className="inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] py-3 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60"
              >
                <Eye size={14} /> {busy === "plan-view" ? "Opening…" : "View"}
              </button>
              <button
                onClick={() => grab("plan", "download")}
                disabled={busy === "plan-download"}
                data-testid="doc-business-plan"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-3 text-sm font-medium hover:bg-[#F6ECCC] transition-colors disabled:opacity-60"
              >
                <Download size={14} /> {busy === "plan-download" ? "Preparing…" : "Download"}
              </button>
            </div>
          </div>

          {/* Lease Agreement */}
          <div className="bg-[#0a0a0a] border border-white/10 p-6 flex flex-col">
            <div className="text-[#D4AF37] mb-4"><ScrollText size={24} /></div>
            <h2 className="font-display text-2xl text-white mb-2" style={{ fontWeight: 400 }}>
              Operating Lease Agreement
            </h2>
            <p className="text-sm text-white/60 leading-relaxed mb-4">
              Template with 9 clauses + Schedule + signature block. Optionally pre-fill lessee,
              vehicle and price from a Purchase Order.
            </p>
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2 block">
                Pre-fill from PO (optional)
              </span>
              <select
                data-testid="doc-lease-po"
                value={selectedPO}
                onChange={(e) => setSelectedPO(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">— Blank template —</option>
                {pos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.po_number} · {p.lessee_name} · {p.vehicle_make} {p.vehicle_model}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-auto grid grid-cols-2 gap-2">
              <button
                onClick={() => grab("lease", "view")}
                disabled={busy === "lease-view"}
                data-testid="doc-lease-agreement-view"
                className="inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] py-3 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60"
              >
                <Eye size={14} /> {busy === "lease-view" ? "Opening…" : "View"}
              </button>
              <button
                onClick={() => grab("lease", "download")}
                disabled={busy === "lease-download"}
                data-testid="doc-lease-agreement"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-3 text-sm font-medium hover:bg-[#F6ECCC] transition-colors disabled:opacity-60"
              >
                <Download size={14} /> {busy === "lease-download" ? "Preparing…" : "Download"}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 text-xs text-white/40 border-l-2 border-[#D4AF37]/40 pl-4 leading-relaxed">
          <b className="text-white/70">Legal notice:</b> Both documents are templates. The
          Operating Lease Agreement must be reviewed and finalised by a qualified Australian
          commercial lawyer before use with any lessee. The Business Plan&apos;s financial figures
          are illustrative — validate with your accountant.
        </div>

        {/* Brand Kit */}
        <section className="mt-16" data-testid="brand-kit-section">
          <div className="flex items-center gap-3 mb-3">
            <Palette size={18} className="text-[#D4AF37]" />
            <div className="label-eyebrow">Brand Kit</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
            <h2 className="font-display text-3xl md:text-4xl text-white" style={{ fontWeight: 300 }}>
              Logo &amp; <span className="italic text-[#D4AF37]">assets</span>
            </h2>
            <div
              data-testid="brand-variant-switch"
              className="inline-flex items-center bg-[#0a0a0a] border border-white/10 p-1"
            >
              <button
                onClick={() => setLogoVariant("dark")}
                data-testid="brand-variant-dark"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                  logoVariant === "dark"
                    ? "bg-[#D4AF37] text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Sun size={12} /> Dark ink
              </button>
              <button
                onClick={() => setLogoVariant("light")}
                data-testid="brand-variant-light"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                  logoVariant === "light"
                    ? "bg-[#D4AF37] text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Moon size={12} /> Knockout
              </button>
            </div>
          </div>
          <p className="text-white/60 text-sm max-w-2xl mb-8">
            Download the Keys to Own logo in three formats. Use <b>Dark ink</b> on light backgrounds
            (letterheads, invoices) and <b>Knockout</b> on dark backgrounds (hero banners, footers,
            social).
          </p>

          {/* Logo preview panel — background swaps with variant */}
          <div
            data-testid="brand-preview"
            className={`relative border p-10 md:p-14 mb-6 flex items-center justify-center overflow-hidden transition-colors duration-500 ${
              logoVariant === "light"
                ? "bg-gradient-to-br from-[#0f0f0f] via-[#080808] to-black border-white/10"
                : "bg-gradient-to-br from-[#F8F5EE] via-[#F1EBD9] to-[#EBE1C4] border-[#D4AF37]/30"
            }`}
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  logoVariant === "light"
                    ? "radial-gradient(circle at 15% 25%, #D4AF37 0%, transparent 45%), radial-gradient(circle at 85% 75%, #D4AF37 0%, transparent 45%)"
                    : "radial-gradient(circle at 15% 25%, #B8862A 0%, transparent 45%), radial-gradient(circle at 85% 75%, #B8862A 0%, transparent 45%)",
              }}
            />
            {logoPreviewUrl ? (
              <img
                src={logoPreviewUrl}
                alt="Keys to Own logo"
                className="relative max-h-32 w-auto transition-opacity duration-500 opacity-100"
                data-testid="brand-preview-img"
              />
            ) : (
              <div
                className={`text-xs tracking-[0.2em] uppercase ${
                  logoVariant === "light" ? "text-white/30" : "text-[#0a0a0a]/40"
                }`}
              >
                Loading logo…
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LogoDownloadTile
              icon={<FileText size={20} />}
              format="PDF"
              tag="Print · Letterheads"
              busy={busy === "logo-pdf-download"}
              onClick={() => grab("logo-pdf", "download")}
              testId="brand-download-pdf"
            />
            <LogoDownloadTile
              icon={<FileImage size={20} />}
              format="PNG"
              tag="Web · Email · Social"
              subtitle="Transparent background · 2400px wide"
              busy={busy === "logo-png-download"}
              onClick={() => grab("logo-png", "download")}
              testId="brand-download-png"
            />
            <LogoDownloadTile
              icon={<ImageIcon size={20} />}
              format="SVG"
              tag="Designer · Infinite scale"
              busy={busy === "logo-svg-download"}
              onClick={() => grab("logo-svg", "download")}
              testId="brand-download-svg"
            />
          </div>

          <div
            className="mt-6 text-xs text-white/40 border-l-2 border-[#D4AF37]/40 pl-4 leading-relaxed"
            data-testid="brand-usage-notes"
          >
            <b className="text-white/70">Colours:</b> Gold <code className="text-[#D4AF37]">#D4AF37</code>{" "}
            (screen) / <code className="text-[#D4AF37]">#B8862A</code> (print) · Ink{" "}
            <code>#111111</code> (dark) / <code>#FFFFFF</code> (knockout) · Glint <code>#F6ECCC</code>.
            Please don&apos;t distort, recolour or place the mark on low-contrast backgrounds.
          </div>
        </section>

        {/* Business cards */}
        <section className="mt-16" data-testid="brand-cards-section">
          <div className="flex items-center gap-3 mb-3">
            <IdCard size={18} className="text-[#D4AF37]" />
            <div className="label-eyebrow">Business Cards</div>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-3" style={{ fontWeight: 300 }}>
            Print-ready <span className="italic text-[#D4AF37]">A4 sheet</span>
          </h2>
          <p className="text-white/60 text-sm max-w-2xl mb-8">
            Generate a full sheet of 10 business cards (90 × 52&nbsp;mm) with the Keys to Own mark,
            your name, title and contact details. Print on 300&nbsp;gsm and trim on the light grey borders.
          </p>

          <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BcField
                label="Name"
                value={bcName}
                onChange={setBcName}
                placeholder="Alex Chen"
                required
                testId="bc-name"
              />
              <BcField
                label="Title"
                value={bcTitle}
                onChange={setBcTitle}
                placeholder="Sales Consultant"
                testId="bc-title"
              />
              <BcField
                label="Email"
                value={bcEmail}
                onChange={setBcEmail}
                placeholder="admin@keystoown.au"
                testId="bc-email"
              />
              <BcField
                label="Phone"
                value={bcPhone}
                onChange={setBcPhone}
                placeholder="0422 254 888"
                testId="bc-phone"
              />
            </div>
            <button
              onClick={() => grab("cards", "download")}
              disabled={busy === "cards-download" || !bcName.trim()}
              data-testid="bc-download"
              className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium px-6 py-3 hover:bg-[#F6ECCC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Download size={14} />
              {busy === "cards-download" ? "Preparing…" : "Download A4 Sheet (10 cards)"}
            </button>
            <p className="mt-4 text-[11px] text-white/40 leading-relaxed">
              Empty fields fall back to the company defaults from your <code>.env</code>{" "}
              (email &amp; phone &amp; ABN). Trim size 90 × 52 mm.
            </p>
          </div>
        </section>

        {/* Google Business Profile / Square logo */}
        <section className="mt-16" data-testid="brand-gbp-section">
          <div className="flex items-center gap-3 mb-3">
            <Building2 size={18} className="text-[#D4AF37]" />
            <div className="label-eyebrow">Google Business Profile · Avatars · Favicons</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
            <h2 className="font-display text-3xl md:text-4xl text-white" style={{ fontWeight: 300 }}>
              Square <span className="italic text-[#D4AF37]">logo</span>
            </h2>
            <div
              data-testid="brand-gbp-bg-switch"
              className="inline-flex items-center bg-[#0a0a0a] border border-white/10 p-1 flex-wrap"
            >
              {[
                { id: "white", label: "White" },
                { id: "gold", label: "Gold" },
                { id: "dark", label: "Dark" },
                { id: "transparent", label: "Transparent" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setGbpBg(opt.id)}
                  data-testid={`brand-gbp-bg-${opt.id}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                    gbpBg === opt.id
                      ? "bg-[#D4AF37] text-black"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-white/60 text-sm max-w-2xl mb-8">
            Square 1:1 logo optimised for <b>Google Business Profile</b> (720 × 720&nbsp;px minimum
            recommended — this exports at 1024). Also perfect for LinkedIn / Instagram / Facebook
            avatars and favicons. Google requires a solid background — we recommend{" "}
            <b>White</b> or <b>Gold</b>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            {/* Preview panel spans 1 column on desktop */}
            <div
              data-testid="brand-gbp-preview"
              className="relative aspect-square border border-white/10 flex items-center justify-center overflow-hidden"
              style={{
                background:
                  gbpBg === "white"
                    ? "#FFFFFF"
                    : gbpBg === "gold"
                    ? "#B8862A"
                    : gbpBg === "dark"
                    ? "#0A0A0A"
                    : "repeating-conic-gradient(#1a1a1a 0deg 90deg, #0a0a0a 90deg 180deg) 0 0/24px 24px",
              }}
            >
              {gbpPreviewUrl ? (
                <img
                  src={gbpPreviewUrl}
                  alt="Square Keys to Own logo preview"
                  className="w-full h-full object-contain"
                  data-testid="brand-gbp-preview-img"
                />
              ) : (
                <div className="text-white/40 text-xs tracking-[0.2em] uppercase">
                  Loading logo…
                </div>
              )}
            </div>

            {/* Download tiles occupy 2 columns on desktop */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LogoDownloadTile
                icon={<FileImage size={20} />}
                format="PNG · 1024 px"
                tag="Google · Social · Favicon"
                subtitle="1024 × 1024 · Ready to upload to Google Business Profile"
                busy={busy === "gbp-png-download"}
                onClick={() => grab("gbp-png", "download")}
                testId="brand-gbp-png"
              />
              <LogoDownloadTile
                icon={<FileText size={20} />}
                format="PDF"
                tag="Print · Letterhead"
                subtitle="Square page · 150 × 150 mm"
                busy={busy === "gbp-pdf-download"}
                onClick={() => grab("gbp-pdf", "download")}
                testId="brand-gbp-pdf"
              />
            </div>
          </div>

          <div
            className="mt-6 text-xs text-white/40 border-l-2 border-[#D4AF37]/40 pl-4 leading-relaxed"
            data-testid="brand-gbp-notes"
          >
            <b className="text-white/70">Google Business Profile spec:</b> square (1:1),
            recommended ≥ 720 px, PNG or JPG, 250 KB – 5 MB. Upload it via
            business.google.com &rarr; <i>Edit profile</i> &rarr; <i>Logo</i>.
          </div>
        </section>

        {/* Social cover banner */}
        <section className="mt-16" data-testid="brand-banner-section">
          <div className="flex items-center gap-3 mb-3">
            <Share2 size={18} className="text-[#D4AF37]" />
            <div className="label-eyebrow">LinkedIn · Facebook · Open Graph</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
            <h2 className="font-display text-3xl md:text-4xl text-white" style={{ fontWeight: 300 }}>
              Social cover <span className="italic text-[#D4AF37]">banner</span>
            </h2>
            <div
              data-testid="brand-banner-bg-switch"
              className="inline-flex items-center bg-[#0a0a0a] border border-white/10 p-1"
            >
              {[
                { id: "dark", label: "Dark" },
                { id: "gold", label: "Gold" },
                { id: "white", label: "White" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setBanBg(opt.id)}
                  data-testid={`brand-banner-bg-${opt.id}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                    banBg === opt.id
                      ? "bg-[#D4AF37] text-black"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-white/60 text-sm max-w-2xl mb-6">
            1200 × 630 px cover banner — sized for LinkedIn company banner,
            Facebook page cover, Twitter/X header, and the Open Graph preview when someone
            shares your site on WhatsApp / Slack.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Live preview */}
            <div className="lg:col-span-3">
              <div
                data-testid="brand-banner-preview"
                className="relative border border-white/10 overflow-hidden"
                style={{ aspectRatio: "1200 / 630" }}
              >
                {banPreviewUrl ? (
                  <img
                    src={banPreviewUrl}
                    alt="Social banner preview"
                    className="w-full h-full object-cover"
                    data-testid="brand-banner-preview-img"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs tracking-[0.2em] uppercase bg-[#0a0a0a]">
                    Rendering preview…
                  </div>
                )}
              </div>
              <p className="text-[11px] text-white/40 mt-2">
                Live preview · updates ~400ms after you finish typing
              </p>
            </div>

            {/* Editor */}
            <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 p-6 flex flex-col gap-5">
              <BcField
                label="Tagline (large italic)"
                value={banTagline}
                onChange={setBanTagline}
                placeholder="Drive now. Own outright."
                testId="banner-tagline"
              />
              <BcField
                label="Subline (small caption)"
                value={banSubline}
                onChange={setBanSubline}
                placeholder="Rent-to-own vehicles for Australian ABN holders."
                testId="banner-subline"
              />
              <button
                onClick={() => grab("banner", "download")}
                disabled={busy === "banner-download"}
                data-testid="brand-banner-download"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium px-4 py-3 hover:bg-[#F6ECCC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Download size={14} />
                {busy === "banner-download" ? "Preparing…" : "Download PNG · 1200 × 630"}
              </button>
              <p className="text-[11px] text-white/40 leading-relaxed">
                Upload on LinkedIn (company page banner), Facebook (page cover), Twitter/X header,
                or drop it in the &lt;meta property=&quot;og:image&quot;&gt; tag on any landing page.
              </p>
            </div>
          </div>

          <div
            className="mt-6 text-xs text-white/40 border-l-2 border-[#D4AF37]/40 pl-4 leading-relaxed"
            data-testid="brand-banner-notes"
          >
            <b className="text-white/70">Social specs:</b> LinkedIn cover 1128 × 191 (top area
            visible), Facebook 820 × 312, Twitter/X 1500 × 500. This 1200 × 630 works well cropped
            to any of these because the key mark + tagline sit inside a safe centre zone.
          </div>
        </section>

        {/* Instagram post */}
        <section className="mt-16" data-testid="brand-ig-section">
          <div className="flex items-center gap-3 mb-3">
            <Instagram size={18} className="text-[#D4AF37]" />
            <div className="label-eyebrow">Instagram · Feed · Story</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
            <h2 className="font-display text-3xl md:text-4xl text-white" style={{ fontWeight: 300 }}>
              Instagram <span className="italic text-[#D4AF37]">post</span>
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {/* Format switch */}
              <div
                data-testid="brand-ig-format-switch"
                className="inline-flex items-center bg-[#0a0a0a] border border-white/10 p-1"
              >
                {[
                  { id: "square", label: "Square", dims: "1080²" },
                  { id: "portrait", label: "Portrait", dims: "4:5" },
                  { id: "story", label: "Story", dims: "9:16" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setIgFormat(opt.id)}
                    data-testid={`brand-ig-format-${opt.id}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-[0.16em] transition-colors ${
                      igFormat === opt.id
                        ? "bg-[#D4AF37] text-black"
                        : "text-white/60 hover:text-white"
                    }`}
                    title={opt.dims}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Background switch */}
              <div
                data-testid="brand-ig-bg-switch"
                className="inline-flex items-center bg-[#0a0a0a] border border-white/10 p-1"
              >
                {[
                  { id: "dark", label: "Dark" },
                  { id: "gold", label: "Gold" },
                  { id: "white", label: "White" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setIgBg(opt.id)}
                    data-testid={`brand-ig-bg-${opt.id}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-[0.16em] transition-colors ${
                      igBg === opt.id
                        ? "bg-[#D4AF37] text-black"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-white/60 text-sm max-w-2xl mb-6">
            Ready-to-post Instagram graphic in three feed formats. Type your headline
            (line breaks welcome), subline and CTA — the preview updates live. Exports at
            1080 px which is Instagram&apos;s native display width.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Preview column */}
            <div className="lg:col-span-2 flex flex-col items-center">
              <div
                data-testid="brand-ig-preview"
                className="relative border border-white/10 overflow-hidden w-full max-w-sm"
                style={{
                  aspectRatio:
                    igFormat === "square" ? "1 / 1"
                    : igFormat === "portrait" ? "4 / 5"
                    : "9 / 16",
                }}
              >
                {igPreviewUrl ? (
                  <img
                    src={igPreviewUrl}
                    alt="Instagram post preview"
                    className="w-full h-full object-cover"
                    data-testid="brand-ig-preview-img"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs tracking-[0.2em] uppercase bg-[#0a0a0a]">
                    Rendering…
                  </div>
                )}
              </div>
              <p className="text-[11px] text-white/40 mt-2 text-center">
                {igFormat === "square" && "1080 × 1080 · Feed square"}
                {igFormat === "portrait" && "1080 × 1350 · Feed portrait (recommended)"}
                {igFormat === "story" && "1080 × 1920 · Story / Reel cover"}
              </p>
            </div>

            {/* Editor column */}
            <div className="lg:col-span-3 bg-[#0a0a0a] border border-white/10 p-6 flex flex-col gap-5">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
                  Headline <span className="text-white/30 normal-case tracking-normal">(Enter for line break)</span>
                </span>
                <textarea
                  data-testid="ig-headline"
                  value={igHeadline}
                  onChange={(e) => setIgHeadline(e.target.value)}
                  placeholder={"Drive now.\nOwn outright."}
                  rows={2}
                  className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none resize-none"
                />
              </label>
              <BcField
                label="Subline"
                value={igSubline}
                onChange={setIgSubline}
                placeholder="No credit checks. 24–48 hour approval."
                testId="ig-subline"
              />
              <BcField
                label="CTA (uppercase pill button)"
                value={igCta}
                onChange={setIgCta}
                placeholder="APPLY AT KEYSTOOWN.AU"
                testId="ig-cta"
              />
              <button
                onClick={() => grab("instagram", "download")}
                disabled={busy === "instagram-download"}
                data-testid="brand-ig-download"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium px-4 py-3 hover:bg-[#F6ECCC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Download size={14} />
                {busy === "instagram-download"
                  ? "Preparing…"
                  : `Download ${igFormat.charAt(0).toUpperCase() + igFormat.slice(1)} · 1080 px`}
              </button>
              <p className="text-[11px] text-white/40 leading-relaxed">
                Tip: Instagram&apos;s algorithm favours <b>portrait 4:5</b> for feed reach — takes
                more real estate on mobile. Use <b>square</b> for grid consistency, or
                <b> story</b> for the Stories / Reels cover frame.
              </p>
            </div>
          </div>

          <div
            className="mt-6 text-xs text-white/40 border-l-2 border-[#D4AF37]/40 pl-4 leading-relaxed"
            data-testid="brand-ig-notes"
          >
            <b className="text-white/70">Native dimensions:</b> Square 1080 × 1080 · Portrait
            1080 × 1350 · Story 1080 × 1920. All export as PNG for the sharpest text — Instagram
            will convert to JPEG on upload.
          </div>
        </section>

        {/* Vehicle Spotlight */}
        <section className="mt-16" data-testid="brand-spot-section">
          <div className="flex items-center gap-3 mb-3">
            <Car size={18} className="text-[#D4AF37]" />
            <div className="label-eyebrow">Vehicle Spotlight · Photo backdrop</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
            <h2 className="font-display text-3xl md:text-4xl text-white" style={{ fontWeight: 300 }}>
              Meet the <span className="italic text-[#D4AF37]">RAV4</span> — from $187/wk
            </h2>
            <div
              data-testid="brand-spot-format-switch"
              className="inline-flex items-center bg-[#0a0a0a] border border-white/10 p-1"
            >
              {[
                { id: "square", label: "Square" },
                { id: "portrait", label: "Portrait" },
                { id: "story", label: "Story" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setSpotFormat(opt.id)}
                  data-testid={`brand-spot-format-${opt.id}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.16em] transition-colors ${
                    spotFormat === opt.id ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-white/60 text-sm max-w-2xl mb-6">
            One-click Instagram post using any car from your catalog as the full-bleed backdrop.
            Weekly price, brand mark and highlights are pulled automatically — override the
            headline if you want a custom hook.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Preview */}
            <div className="lg:col-span-2 flex flex-col items-center">
              <div
                data-testid="brand-spot-preview"
                className="relative border border-white/10 overflow-hidden w-full max-w-sm bg-[#050505]"
                style={{
                  aspectRatio:
                    spotFormat === "square" ? "1 / 1"
                    : spotFormat === "portrait" ? "4 / 5"
                    : "9 / 16",
                }}
              >
                {spotPreviewUrl ? (
                  <img
                    src={spotPreviewUrl}
                    alt="Vehicle spotlight preview"
                    className="w-full h-full object-cover"
                    data-testid="brand-spot-preview-img"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs tracking-[0.2em] uppercase">
                    Loading photo…
                  </div>
                )}
              </div>
              <p className="text-[11px] text-white/40 mt-2 text-center">
                Preview loads photo from catalog · debounced ~600 ms
              </p>
            </div>

            {/* Editor */}
            <div className="lg:col-span-3 bg-[#0a0a0a] border border-white/10 p-6 flex flex-col gap-5">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
                  Vehicle <span className="text-[#D4AF37]">*</span>
                </span>
                <select
                  data-testid="spot-vehicle-select"
                  value={spotVehicleId}
                  onChange={(e) => {
                    setSpotVehicleId(e.target.value);
                    setSpotHighlightIdx(-1);
                  }}
                  className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                >
                  {vehicles.length === 0 && <option value="">Loading catalog…</option>}
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.year} {v.make} {v.model} · ${v.weekly_from}/wk
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
                  Headline override <span className="text-white/30 normal-case tracking-normal">(blank = auto)</span>
                </span>
                <input
                  type="text"
                  data-testid="spot-headline"
                  value={spotHeadline}
                  onChange={(e) => setSpotHeadline(e.target.value)}
                  placeholder="Meet the RAV4 Hybrid."
                  className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
                  Feature line
                </span>
                <select
                  data-testid="spot-highlight-select"
                  value={spotHighlightIdx}
                  onChange={(e) => setSpotHighlightIdx(parseInt(e.target.value, 10))}
                  className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                >
                  <option value={-1}>All three highlights (joined)</option>
                  {(vehicles.find((v) => v.id === spotVehicleId)?.highlights || []).map((h, i) => (
                    <option key={i} value={i}>{h}</option>
                  ))}
                </select>
              </label>

              <BcField
                label="CTA (outline pill button)"
                value={spotCta}
                onChange={setSpotCta}
                placeholder="APPLY AT KEYSTOOWN.AU"
                testId="spot-cta"
              />

              <button
                onClick={() => grab("spotlight", "download")}
                disabled={busy === "spotlight-download" || !spotVehicleId}
                data-testid="brand-spot-download"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-medium px-4 py-3 hover:bg-[#F6ECCC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Download size={14} />
                {busy === "spotlight-download"
                  ? "Preparing…"
                  : `Download ${spotFormat.charAt(0).toUpperCase() + spotFormat.slice(1)} · 1080 px`}
              </button>

              <p className="text-[11px] text-white/40 leading-relaxed">
                Photos are pulled from the Wikimedia catalog on each render (~1–2s the first
                time). Weekly-from prices flow from the same catalog used on your homepage.
              </p>
            </div>
          </div>

          <div
            className="mt-6 text-xs text-white/40 border-l-2 border-[#D4AF37]/40 pl-4 leading-relaxed"
            data-testid="brand-spot-notes"
          >
            <b className="text-white/70">Pro tip:</b> Post the <b>portrait</b> variant to feed
            (fills more phone screen), and post the <b>story</b> variant to Stories on the same
            day for double exposure.
          </div>
        </section>
      </main>
    </div>
  );
}

function BcField({ label, value, onChange, placeholder, required, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
      />
    </label>
  );
}

function LogoDownloadTile({ icon, format, tag, subtitle, busy, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="group text-left bg-[#0a0a0a] border border-white/10 hover:border-[#D4AF37]/60 p-5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[#D4AF37]">{icon}</div>
        <Download size={14} className="text-white/40 group-hover:text-[#D4AF37] transition-colors" />
      </div>
      <div className="font-display text-2xl text-white mb-1" style={{ fontWeight: 400 }}>
        {format}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80 mb-2">{tag}</div>
      {subtitle && <div className="text-xs text-white/50 leading-relaxed">{subtitle}</div>}
      <div className="text-xs text-white/60 mt-3">
        {busy ? "Preparing…" : "Download"}
      </div>
    </button>
  );
}
